import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Power } from "lucide-react";

export default function MaintenanceControl({ token }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("now");
  const [amount, setAmount] = useState(30);
  const [unit, setUnit] = useState("minutes");
  const [startAt, setStartAt] = useState("");
  const [message, setMessage] = useState("");
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => axios.get(`${API}/platform/status`).then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []); // eslint-disable-line
  const mult = { minutes: 1, heures: 60, jours: 1440 };
  const activate = async () => {
    const body = { duration_minutes: Math.max(1, parseInt(amount) || 0) * mult[unit], message };
    if (mode === "scheduled") {
      if (!startAt) { toast.error("Choisissez une date et une heure de début"); return; }
      body.scheduled_start = new Date(startAt).toISOString();
    } else {
      body.enabled = true;
    }
    try {
      await axios.post(`${API}/superadmin/maintenance`, body, h);
      toast.success(mode === "scheduled" ? "Maintenance programmée — les prestataires seront prévenus par email" : "Application coupée — mode maintenance activé");
      setOpen(false); load();
    } catch { toast.error("Échec de l'opération"); }
  };
  const deactivate = async () => {
    try { await axios.post(`${API}/superadmin/maintenance`, { enabled: false }, h); toast.success("Maintenance annulée"); load(); }
    catch { toast.error("Échec de l'annulation"); }
  };
  const active = status?.maintenance;
  const scheduled = status?.scheduled_start;
  return (
    <>
      {active ? (
        <button data-testid="sa-maintenance-off" onClick={deactivate} className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1.5 text-sm font-medium text-emerald-400 hover:bg-emerald-500/25"><Power className="h-4 w-4" />Réactiver l'app</button>
      ) : scheduled ? (
        <div className="flex items-center gap-2">
          <span data-testid="sa-maintenance-scheduled" className="rounded-full bg-amber-500/15 px-3 py-1.5 text-sm font-medium text-amber-400">Maintenance prévue le {new Date(scheduled).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <button data-testid="sa-maintenance-cancel" onClick={deactivate} className="rounded-full bg-zinc-800 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700">Annuler</button>
        </div>
      ) : (
        <button data-testid="sa-maintenance-on" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-full bg-red-500/15 px-3 py-1.5 text-sm font-medium text-red-400 hover:bg-red-500/25"><Power className="h-4 w-4" />Couper l'app</button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-zinc-800 bg-zinc-950 text-white">
          <DialogHeader><DialogTitle>Couper l'application</DialogTitle><DialogDescription className="text-zinc-400">Active la maintenance et bloque les connexions.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Quand ?</Label>
              <div className="mt-1.5 flex gap-2">
                <button type="button" data-testid="sa-maintenance-mode-now" onClick={() => setMode("now")} className={`flex-1 rounded-full px-3 py-2 text-sm font-medium ${mode === "now" ? "bg-[#FF6A00] text-white" : "bg-zinc-900 text-zinc-400"}`}>Immédiatement</button>
                <button type="button" data-testid="sa-maintenance-mode-scheduled" onClick={() => setMode("scheduled")} className={`flex-1 rounded-full px-3 py-2 text-sm font-medium ${mode === "scheduled" ? "bg-[#FF6A00] text-white" : "bg-zinc-900 text-zinc-400"}`}>Programmer</button>
              </div>
            </div>
            {mode === "scheduled" && (
              <div>
                <Label className="text-zinc-400">Date et heure de début</Label>
                <Input data-testid="sa-maintenance-start" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="mt-1.5 border-zinc-800 bg-zinc-900 [color-scheme:dark]" />
                <p className="mt-1 text-xs text-zinc-500">Un email d'avertissement sera envoyé automatiquement aux prestataires ~30 min avant la coupure.</p>
              </div>
            )}
            <div>
              <Label className="text-zinc-400">Durée estimée de la mise à jour</Label>
              <div className="mt-1.5 flex gap-2">
                <Input data-testid="sa-maintenance-duration" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-28 border-zinc-800 bg-zinc-900" />
                <select data-testid="sa-maintenance-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-white">
                  <option value="minutes">minutes</option><option value="heures">heures</option><option value="jours">jours</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Message affiché (optionnel)</Label>
              <Textarea data-testid="sa-maintenance-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Nous améliorons HalgarOS, retour très vite !" className="mt-1.5 border-zinc-800 bg-zinc-900" />
            </div>
            <Button data-testid="sa-maintenance-confirm" onClick={activate} className="w-full rounded-full bg-red-500 font-semibold text-white hover:bg-red-600"><Power className="mr-1.5 h-4 w-4" />{mode === "scheduled" ? "Programmer la maintenance" : "Couper l'application maintenant"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
